import { FormEvent, useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { track } from '../lib/analytics'
import { useEvent, useEventOccurrences, useCreateRsvp, useUpdateRsvp, useDeleteRsvp, useMyRsvp } from '../hooks/usePotlucks'
import { useOccurrenceRsvps, useEventMetrics, useRecordEventMetric, usePublishEvent, useDuplicateEvent, useSetRsvpDeadline } from '../hooks/useChurchAdmin'
import { useChurch, useTrackEventView, useTrackPlanToAttend, useInquireChurch } from '../hooks/useChurches'
import { useAuthStore } from '../store/auth'
import { PublicLayout } from '../components/layout/PublicLayout'
import { EventBadge } from '../components/ui/EventBadge'
import { Icons } from '../components/ui/Icons'
import { DishSegments } from '../components/ui/DishCoverage'
import type { DishItem } from '../components/ui/DishCoverage'
import { getEventType } from '../components/ui/eventTypes'
import { getDefaultImageForType } from '../components/ui/EventImagePicker'
import { EventLocationMap } from '../components/EventLocationMap'
import { ShareDrawer } from '../components/ShareDrawer'
import type { DishCategory, EventOccurrence, FellowshipEvent, EventAttributes } from '@fellowship/shared'
import { getSessionId } from '../lib/sessionId'

const ATTRIBUTE_LABELS: Partial<Record<keyof EventAttributes, string>> = {
  kidFriendly: 'Kid friendly',
  petFriendly: 'Pet friendly',
  vegetarianFriendly: 'Vegetarian',
  veganFriendly: 'Vegan',
  glutenFreeOptions: 'Gluten-free options',
  allWelcome: 'All welcome',
  noMeat: 'No meat',
  noAlcohol: 'No alcohol',
  noTobacco: 'No tobacco',
  accessibilityFriendly: 'Accessible',
}

const DISH_CATEGORIES: { value: DishCategory; label: string }[] = [
  { value: 'none', label: "Decide later" },
  { value: 'main', label: 'Mains' },
  { value: 'salad', label: 'Salad' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'drink', label: 'Drinks' },
  { value: 'side', label: 'Side' },
]

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function getRecurrenceLabel(rrule: string | null | undefined): string {
  if (!rrule) return 'Recurring'
  if (rrule.includes('FREQ=WEEKLY')) return 'Weekly'
  if (rrule.includes('FREQ=BIWEEKLY') || rrule.includes('INTERVAL=2')) return 'Bi-weekly'
  if (rrule.includes('FREQ=MONTHLY')) return 'Monthly'
  if (rrule.includes('FREQ=DAILY')) return 'Daily'
  return 'Recurring'
}

function DetailTile({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="p-4 bg-surface rounded-[14px] border border-line-soft">
      <div className="flex items-center gap-1.5 text-muted mb-1.5">
        {icon}
        <span className="eyebrow" style={{ fontSize: 10 }}>{label}</span>
      </div>
      <div className="text-[15px] font-semibold text-ink">{value}</div>
      {sub && <div className="text-[11.5px] text-muted mt-0.5">{sub}</div>}
    </div>
  )
}

interface RsvpFormProps {
  occurrence: EventOccurrence
  event: FellowshipEvent
  onSuccess: (id: string) => void
}

function RsvpForm({ occurrence, event, onSuccess }: RsvpFormProps) {
  const { accessToken } = useAuthStore()
  const createRsvp = useCreateRsvp()
  const [dishCategory, setDishCategory] = useState<DishCategory>('none')
  const [dishDescription, setDishDescription] = useState('')
  const [guestsCount, setGuestsCount] = useState(1)
  const [note, setNote] = useState('')
  const [guestName, setGuestName] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [guestSuccess, setGuestSuccess] = useState(false)
  const [guestManageUrl, setGuestManageUrl] = useState<string | null>(null)
  const t = getEventType(event.eventType ?? 'potluck')
  const isApprovalRequired = event.rsvpRequiresApproval

  if (!accessToken && event.visibility === 'invite_only') {
    return (
      <div className="text-center py-3">
        <p className="text-[13px] text-muted mb-3.5">Sign in to RSVP for this event.</p>
        <Link to="/login">
          <button className="btn btn--full btn--lg" style={{ background: t.color }}>
            Log in to RSVP
          </button>
        </Link>
      </div>
    )
  }

  if (!accessToken && guestSuccess) {
    return (
      <div className="flex flex-col gap-3.5">
        <div className="px-4 py-3.5 rounded-xl bg-accent-soft border border-accent">
          <p className="m-0 text-[15px] font-semibold text-ink">You're going!</p>
          <p className="m-0 mt-1 text-[12.5px] text-ink-2">
            {isApprovalRequired
              ? 'Your request is pending. The host will review it soon.'
              : "You're on the list. Save the link below to update your RSVP."}
          </p>
        </div>
        {guestManageUrl && (
          <a
            href={guestManageUrl}
            className="text-[13px] font-medium text-primary break-all"
          >
            Manage your RSVP →
          </a>
        )}
        <p className="m-0 text-[12.5px] text-muted">
          Create a free account to get reminders, update your dish, and discover more events nearby.
        </p>
        <Link to={`/register?email=${encodeURIComponent(guestEmail)}`}>
          <button className="btn btn--full" style={{ background: t.color }}>
            Create account — it's free
          </button>
        </Link>
        <p className="m-0 text-center text-[11.5px] text-muted">
          Already have an account?{' '}
          <Link to="/login" className="text-primary">Log in</Link>
        </p>
      </div>
    )
  }

  const isPotluck = event.eventType === 'potluck' || Boolean(event.attributes?.bringADish)

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    createRsvp.mutate(
      {
        occurrenceId: occurrence.id,
        attendanceStatus: 'going',
        ...(isPotluck ? { dishCategory, dishDescription: dishDescription || undefined } : {}),
        guestsCount,
        note: note || undefined,
        ...(!accessToken ? { guestName, guestEmail } : {}),
      },
      {
        onSuccess: (res) => {
          track('rsvp_submitted', { event_type: event.eventType, is_guest: !accessToken })
          if (accessToken) {
            onSuccess(res.data.rsvp.id)
          } else {
            setGuestManageUrl(res.data.manageUrl ?? null)
            setGuestSuccess(true)
          }
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {!accessToken && (
        <>
          <div>
            <div className="eyebrow mb-2">Your name</div>
            <input
              className="input"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Jane Smith"
              required
              aria-label="Your name"
            />
          </div>
          <div>
            <div className="eyebrow mb-2">Email</div>
            <input
              type="email"
              className="input"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="jane@example.com"
              required
              aria-label="Email address"
            />
          </div>
        </>
      )}

      {isApprovalRequired && event.hostedByType !== 'church' && (
        <div className="px-3.5 py-2.5 rounded-[10px] bg-accent-soft border border-accent text-[12.5px] text-ink-2">
          {event.hideAddressUntilApproved
            ? 'This host reviews RSVPs. You\'ll receive the full address once approved.'
            : 'This host reviews each RSVP before confirming your spot.'}
        </div>
      )}

      {isPotluck && (
        <>
          <div>
            <div className="eyebrow mb-2">Category</div>
            <div className="flex flex-wrap gap-1.5">
              {DISH_CATEGORIES.map((c) => (
                <button
                  key={c.value} type="button"
                  onClick={() => setDishCategory(c.value)}
                  className={`chip h-[30px] text-[12.5px] px-[11px] cursor-pointer ${dishCategory === c.value ? 'chip--solid' : 'border-line-soft'}`}
                >{c.label}</button>
              ))}
            </div>
          </div>

          {dishCategory !== 'none' && (
            <div>
              <div className="eyebrow mb-2">What you'll bring</div>
              <input
                className="input"
                value={dishDescription}
                onChange={(e) => setDishDescription(e.target.value)}
                placeholder="e.g. Caesar salad, haystacks…"
              />
            </div>
          )}
        </>
      )}

      <div>
        <div className="eyebrow mb-2">Guests including you</div>
        <div className="flex items-center gap-2.5">
          <button type="button" className="stepper-btn" onClick={() => setGuestsCount(Math.max(1, guestsCount - 1))}>
            <Icons.minus size={14} />
          </button>
          <div className="serif text-[26px] w-[42px] text-center">{guestsCount}</div>
          <button type="button" className="stepper-btn" onClick={() => setGuestsCount(guestsCount + 1)}>
            <Icons.plus size={14} />
          </button>
        </div>
      </div>

      <div>
        <div className="eyebrow mb-2">
          Note to host <span className="normal-case tracking-normal text-muted text-[11px]">(optional)</span>
        </div>
        <textarea
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          style={{ height: 'auto', padding: '10px 12px', resize: 'none' }}
        />
      </div>

      {createRsvp.isError && (
        <p role="alert" className="text-[12.5px] text-warn m-0">
          {(createRsvp.error as Error).message}
        </p>
      )}

      <button
        type="submit"
        disabled={createRsvp.isPending}
        className="btn btn--lg btn--full"
        style={{ background: t.color }}
      >
        {createRsvp.isPending ? 'Submitting…'
          : isApprovalRequired ? 'Request to join'
          : "RSVP — I'm going"}
        <Icons.arrow size={14} />
      </button>

      {!accessToken ? (
        <p className="m-0 text-center text-[11.5px] text-muted">
          Already have an account?{' '}
          <Link to="/login" className="text-primary">Log in</Link>
        </p>
      ) : (
        <div className="text-center text-[11.5px] text-muted">
          You'll get a reminder the day before
        </div>
      )}
    </form>
  )
}

function HostPanel({ event, occurrence, eventId, adminEditUrl }: {
  event: FellowshipEvent
  occurrence: EventOccurrence
  eventId: string
  adminEditUrl: string | null
}) {
  const navigate = useNavigate()
  const t = getEventType(event.eventType ?? 'potluck')
  const editUrl = adminEditUrl ?? `/events/${eventId}/edit`
  const { data } = useOccurrenceRsvps(event.status === 'draft' ? undefined : occurrence.id)
  const { data: metricsData } = useEventMetrics(event.status === 'draft' ? undefined : eventId)
  const publish = usePublishEvent(eventId)
  const duplicate = useDuplicateEvent()
  const setDeadline = useSetRsvpDeadline(occurrence.id)
  const [showDeadlinePicker, setShowDeadlinePicker] = useState(false)
  const [deadlineInput, setDeadlineInput] = useState('')

  const now = new Date()
  const rsvpClosesAt = occurrence.rsvpClosesAt ? new Date(occurrence.rsvpClosesAt) : null
  const rsvpIsClosed = rsvpClosesAt != null && rsvpClosesAt <= now

  const isDraft = event.status === 'draft'
  const rsvps = data?.data.rsvps ?? []
  const goingCount = rsvps.filter((r) => r.attendanceStatus === 'going' && r.approvalStatus !== 'declined').length
  const maybeCount = rsvps.filter((r) => r.attendanceStatus === 'maybe' && r.approvalStatus !== 'declined').length
  const pendingCount = rsvps.filter((r) => r.approvalStatus === 'pending').length

  const isPotluck = event.eventType === 'potluck' || Boolean(event.attributes?.bringADish)
  const dishProgress = data?.data.dishProgress ?? null
  const goals = dishProgress ? Object.entries(dishProgress.goals).filter(([k]) => k !== 'bringPctGoal') : []
  const totalClaimed = goals.reduce((s, [cat]) => s + ((dishProgress!.claimed as Record<string, number>)[cat] ?? 0), 0)
  const totalNeeded = goals.reduce((s, [, need]) => s + (need as number), 0)

  return (
    <div className="card p-[22px]" style={{ borderLeft: `3px solid ${isDraft ? 'var(--muted)' : t.color}` }}>
      <div className="flex items-center justify-between mb-[18px]">
        <div>
          <div className="eyebrow mb-1" style={{ color: isDraft ? 'var(--muted)' : t.color }}>You're hosting</div>
          <div className="serif text-[22px]">{isDraft ? 'Draft event' : 'Guest list'}</div>
        </div>
        <span
          className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
          style={isDraft
            ? { background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--line-soft)' }
            : { background: t.color, color: '#fff' }}
        >
          {isDraft ? 'Draft' : 'Hosting'}
        </span>
      </div>

      {isDraft && (
        <div className="mb-4 px-3 py-2.5 rounded-[10px] bg-s2 border border-line-soft text-[13px] text-ink-2">
          This event is saved as a draft. It's not visible to anyone else yet.
        </div>
      )}

      <div className={`flex gap-5 ${pendingCount > 0 ? 'mb-2.5' : 'mb-[18px]'}`}>
        <div>
          <div className="serif text-[28px] leading-none">{goingCount}</div>
          <div className="text-[11px] text-muted mt-0.5">going</div>
        </div>
        <div>
          <div className="serif text-[28px] leading-none">{maybeCount}</div>
          <div className="text-[11px] text-muted mt-0.5">maybe</div>
        </div>
      </div>

      {pendingCount > 0 && (
        <div className="px-3 py-2 rounded-[10px] mb-[18px] bg-accent-soft border border-accent text-[12.5px] text-warn font-medium flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-warn text-white text-[10px] font-bold shrink-0">
            {pendingCount}
          </span>
          pending approval{pendingCount !== 1 ? 's' : ''} need attention
        </div>
      )}

      {isPotluck && dishProgress && totalNeeded > 0 && (
        <div className="mb-[18px]">
          <div className="flex justify-between items-baseline mb-1.5">
            <div className="eyebrow" style={{ fontSize: 10 }}>Dish coverage</div>
            <div className={`mono text-[11px] ${totalClaimed >= totalNeeded ? 'text-good' : 'text-warn'}`}>
              {totalClaimed}/{totalNeeded}
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-line-soft overflow-hidden">
            <div
              className="h-full rounded-full transition-[width_0.3s]"
              style={{
                width: `${Math.min(100, Math.round((totalClaimed / totalNeeded) * 100))}%`,
                background: totalClaimed >= totalNeeded ? 'var(--good)' : t.color,
              }}
            />
          </div>
        </div>
      )}

      <Link to={`/events/${eventId}/occurrences/${occurrence.id}/manage`} className="no-underline">
        <button className="btn btn--full btn--lg mb-2.5" style={{ background: t.color }}>
          Manage guests <Icons.arrow size={14} />
        </button>
      </Link>
      <div className="text-center text-[11.5px] text-muted mb-3">
        {event.rsvpRequiresApproval ? 'Approve RSVPs, send broadcasts, export CSV' : 'View guests, send broadcasts, export CSV'}
      </div>
      {metricsData && (
        <div className="mt-[18px] pt-[18px] border-t border-line-soft">
          <div className="eyebrow mb-2.5" style={{ fontSize: 10 }}>Last 30 days</div>
          <div className="flex gap-5">
            <div>
              <div className="serif text-[22px] leading-none">{metricsData.data.views}</div>
              <div className="text-[11px] text-muted mt-0.5">views</div>
            </div>
            <div>
              <div className="serif text-[22px] leading-none">{metricsData.data.impressions}</div>
              <div className="text-[11px] text-muted mt-0.5">impressions</div>
            </div>
            <div>
              <div className="serif text-[22px] leading-none">{metricsData.data.rsvps}</div>
              <div className="text-[11px] text-muted mt-0.5">RSVPs</div>
            </div>
          </div>
        </div>
      )}

      {/* RSVP deadline section */}
      {!isDraft && (
        <div className="mt-[18px] pt-[18px] border-t border-line-soft">
          <div className="eyebrow mb-2" style={{ fontSize: 10 }}>RSVP deadline</div>
          {rsvpIsClosed ? (
            <div>
              <div className="text-[12.5px] text-warn font-medium mb-2">
                RSVPs closed · {rsvpClosesAt!.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
              <button
                onClick={() => setDeadline.mutate(null)}
                disabled={setDeadline.isPending}
                className="text-[12px] text-primary cursor-pointer bg-transparent border-0 p-0 font-sans font-medium"
              >
                Reopen RSVPs
              </button>
            </div>
          ) : rsvpClosesAt ? (
            <div>
              <div className="text-[12.5px] text-ink-2 mb-2">
                Closes {rsvpClosesAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeadline.mutate(null)}
                  disabled={setDeadline.isPending}
                  className="text-[12px] text-primary cursor-pointer bg-transparent border-0 p-0 font-sans font-medium"
                >
                  Remove deadline
                </button>
                <span className="text-muted text-[12px]">·</span>
                <button
                  onClick={() => { setDeadline.mutate(new Date().toISOString()) }}
                  disabled={setDeadline.isPending}
                  className="text-[12px] text-warn cursor-pointer bg-transparent border-0 p-0 font-sans font-medium"
                >
                  Close now
                </button>
              </div>
            </div>
          ) : showDeadlinePicker ? (
            <div className="flex flex-col gap-2">
              <input
                type="datetime-local"
                value={deadlineInput}
                onChange={(e) => setDeadlineInput(e.target.value)}
                className="px-2.5 py-2 rounded-lg border border-line-soft bg-bg text-[12.5px] font-sans w-full"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (deadlineInput) {
                      setDeadline.mutate(new Date(deadlineInput).toISOString(), {
                        onSuccess: () => { setShowDeadlinePicker(false); setDeadlineInput('') },
                      })
                    }
                  }}
                  disabled={setDeadline.isPending || !deadlineInput}
                  className="text-[12px] text-primary cursor-pointer bg-transparent border-0 p-0 font-sans font-medium disabled:opacity-50"
                >
                  {setDeadline.isPending ? 'Saving…' : 'Set deadline'}
                </button>
                <span className="text-muted text-[12px]">·</span>
                <button
                  onClick={() => { setDeadline.mutate(new Date().toISOString()) }}
                  disabled={setDeadline.isPending}
                  className="text-[12px] text-warn cursor-pointer bg-transparent border-0 p-0 font-sans font-medium"
                >
                  Close now
                </button>
                <span className="text-muted text-[12px]">·</span>
                <button
                  onClick={() => setShowDeadlinePicker(false)}
                  className="text-[12px] text-muted cursor-pointer bg-transparent border-0 p-0 font-sans"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeadlinePicker(true)}
                className="text-[12px] text-ink-2 cursor-pointer bg-transparent border-0 p-0 font-sans font-medium hover:text-primary"
              >
                Set deadline
              </button>
              <span className="text-muted text-[12px]">·</span>
              <button
                onClick={() => { setDeadline.mutate(new Date().toISOString()) }}
                disabled={setDeadline.isPending}
                className="text-[12px] text-warn cursor-pointer bg-transparent border-0 p-0 font-sans font-medium"
              >
                Close now
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-[18px] flex flex-col gap-2">
        {isDraft && (
          <button
            onClick={() => publish.mutate(undefined, {
              onSuccess: () => navigate(`/events/${eventId}`),
            })}
            disabled={publish.isPending}
            className="btn btn--full"
            style={{ background: t.color, color: '#fff', border: 'none' }}
          >
            {publish.isPending ? 'Publishing…' : '🚀 Publish event'}
          </button>
        )}
        <div className="flex gap-3 justify-center">
          <Link
            to={editUrl}
            className="text-[12.5px] text-ink-2 no-underline font-medium"
          >
            Edit →
          </Link>
          <span className="text-muted">·</span>
          <button
            onClick={() => duplicate.mutate(eventId, {
              onSuccess: (res) => navigate(`/events/${res.data.event.id}`),
            })}
            disabled={duplicate.isPending}
            className="text-[12.5px] text-ink-2 font-medium cursor-pointer bg-transparent border-none p-0 font-sans"
          >
            {duplicate.isPending ? 'Duplicating…' : 'Duplicate →'}
          </button>
        </div>
      </div>
    </div>
  )
}

function HostedByCard({ hostedByType, hostedById }: { hostedByType: string; hostedById: string }) {
  const { data: churchData } = useChurch(hostedByType === 'church' ? hostedById : undefined)
  const church = churchData?.data

  if (hostedByType !== 'church' || !church) return null

  return (
    <div className="mt-8">
      <div className="eyebrow mb-3">Hosted by</div>
      <Link to={`/churches/${church.slug}`} className="no-underline block">
        <div className="card p-5 flex items-center gap-4 hover:border-primary transition-colors">
          <div className="w-12 h-12 rounded-xl bg-primary-soft flex items-center justify-center text-primary font-bold text-[18px] serif shrink-0">
            {church.name.slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-semibold text-ink">{church.name}</div>
            <div className="text-[12.5px] text-muted mt-0.5">
              {[church.denomination, church.city, church.state].filter(Boolean).join(' · ')}
            </div>
          </div>
          <Icons.arrow size={14} className="text-muted shrink-0" />
        </div>
      </Link>
    </div>
  )
}

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: eventData, isLoading: eventLoading } = useEvent(id)
  const { data: occurrencesData, isLoading: occLoading } = useEventOccurrences(id)
  const updateRsvp = useUpdateRsvp()
  const deleteRsvp = useDeleteRsvp()
  const [rsvpSuccess, setRsvpSuccess] = useState(false)
  const [activeRsvpId, setActiveRsvpId] = useState<string | undefined>()
  const [localAttendance, setLocalAttendance] = useState<'going' | 'maybe' | 'not_going' | null>(null)
  const { user, accessToken } = useAuthStore()

  const event = eventData?.data
  const occurrences = occurrencesData?.data ?? []
  const nextOccurrence = occurrences.find((o) => !o.isCancelled)
  const t = getEventType(event?.eventType ?? 'potluck')
  const isOwnEvent = !!(user && event?.hostedByType === 'person' && event?.hostedById === user.id)

  const _now = new Date()
  const isPast = nextOccurrence ? new Date(nextOccurrence.startsAt) <= _now : (occurrences.length > 0 && !occurrences.find((o) => !o.isCancelled && new Date(o.startsAt) > _now))
  const isRsvpClosed = isPast || (nextOccurrence?.rsvpClosesAt != null && new Date(nextOccurrence.rsvpClosesAt) <= _now)

  // Fetch hosting church for denomination enforcement and admin edit URL (must be before early returns)
  const { data: hostingChurchData } = useChurch(
    event?.hostedByType === 'church' ? event.hostedById : undefined,
  )
  const hostingChurchSlug = hostingChurchData?.data?.slug
  const adminEditUrl = event && event.hostedByType === 'church' && hostingChurchSlug
    ? `/churches/${hostingChurchSlug}/events/${event.id}/edit`
    : null

  const { data: myRsvpData, isLoading: myRsvpLoading } = useMyRsvp(nextOccurrence?.id)
  const existingRsvp = myRsvpData?.data?.rsvp ?? null

  const trackView = useTrackEventView(id)
  const trackPlanToAttend = useTrackPlanToAttend(id)
  const inquire = useInquireChurch(id)
  const recordMetric = useRecordEventMetric()
  const [showInquiryForm, setShowInquiryForm] = useState(false)
  const [showShare, setShowShare] = useState(false)
  useEffect(() => {
    if (!event) return
    if (event.source === 'scraped') trackView.mutate()
    recordMetric.mutate({
      eventId: event.id,
      metricType: 'view',
      sessionId: getSessionId(),
      occurrenceId: nextOccurrence?.id,
    })
  }, [event?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (eventLoading || occLoading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-[13px] text-muted">Loading…</p>
        </div>
      </PublicLayout>
    )
  }

  if (!event) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center flex-col gap-3" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-[14px] text-ink-2">Event not found.</p>
          <Link to="/search" className="text-primary text-[13px] no-underline font-medium">
            ← Back to search
          </Link>
        </div>
      </PublicLayout>
    )
  }

  const isChurchHosted = event.hostedByType === 'church'
  const isScraped = event.source === 'scraped'

  const isDenominationRestricted =
    !!hostingChurchData?.data &&
    event.audience === 'denomination' &&
    isChurchHosted &&
    user?.denomination !== hostingChurchData.data.denomination

  const dishItems: DishItem[] | null = (!isScraped && nextOccurrence?.dishProgress?.goals)
    ? Object.entries(nextOccurrence.dishProgress.goals)
        .filter(([k]) => k !== 'bringPctGoal')
        .map(([cat, need]) => ({
          name: cat.charAt(0).toUpperCase() + cat.slice(1),
          need: need as number,
          got: (nextOccurrence.dishProgress!.claimed as Record<string, number>)[cat] ?? 0,
          icon: cat === 'main' ? '🍽' : cat === 'salad' ? '🥗' : cat === 'dessert' ? '🍰' : cat === 'drink' ? '🥤' : '🥘',
        }))
    : null

  const totalClaimed = dishItems?.reduce((s, d) => s + d.got, 0) ?? 0
  const totalNeeded = dishItems?.reduce((s, d) => s + d.need, 0) ?? 0

  const canonicalUrl = `https://fellowship-app.com/events/${event.id}`
  const pageTitle = `${event.title} · Fellowship`
  const pageDescription = event.description
    ? event.description.slice(0, 160)
    : `${t.label} hosted by ${hostingChurchData?.data?.name ?? 'a local community'}${nextOccurrence ? ` on ${new Date(nextOccurrence.startsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}` : ''}.`
  const pageImage = event.imageUrl ?? getDefaultImageForType(event.eventType ?? 'potluck', '800x480')

  const eventJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title,
    description: event.description ?? pageDescription,
    url: canonicalUrl,
    image: pageImage,
    ...(nextOccurrence && {
      startDate: nextOccurrence.startsAt,
      endDate: nextOccurrence.endsAt ?? nextOccurrence.startsAt,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    }),
    location: {
      '@type': 'Place',
      address: event.addressText ?? '',
    },
    ...(hostingChurchData?.data && {
      organizer: {
        '@type': 'Organization',
        name: hostingChurchData.data.name,
        url: `https://fellowship-app.com/churches/${hostingChurchData.data.slug}`,
      },
    }),
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="event" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={pageImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
        <script type="application/ld+json">{JSON.stringify(eventJsonLd)}</script>
      </Helmet>
    <PublicLayout>
      {/* Type-tinted hero banner */}
      <div style={{
        background: `linear-gradient(135deg, ${t.color}18 0%, ${t.color}08 100%)`,
        borderBottom: `1px solid ${t.color}22`,
      }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '14px 20px 12px' }}>
          <Link to="/search" className="text-[13px] text-muted inline-flex items-center gap-1.5 no-underline">
            <Icons.back size={13} /> All events · this weekend
          </Link>
        </div>
      </div>

      {event.status === 'draft' && (
        <div style={{ background: '#FFF7ED', borderBottom: '1px solid #FED7AA' }}>
          <div style={{ maxWidth: 1240, margin: '0 auto', padding: '10px 20px', fontSize: 13, color: '#92400E', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>📝</span>
            <span>This event is a <strong>draft</strong> — only you can see it.</span>
            {adminEditUrl && (
              <Link to={adminEditUrl} style={{ color: '#92400E', fontWeight: 600, textDecoration: 'underline' }}>
                Edit event →
              </Link>
            )}
          </div>
        </div>
      )}

      {isDenominationRestricted && (
        <div style={{ background: '#FFF7ED', borderBottom: '1px solid #FED7AA' }}>
          <div style={{ maxWidth: 1240, margin: '0 auto', padding: '10px 20px', fontSize: 13, color: '#92400E', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🏷</span>
            <span>
              This event is for <strong>{hostingChurchData?.data?.denomination?.replace(/-/g, ' ')}</strong> members.
              {!user && ' Sign in and set your denomination in your profile to RSVP.'}
              {user && ' Update your denomination in your profile to RSVP.'}
            </span>
          </div>
        </div>
      )}

      {isPast && (
        <div style={{ background: '#F5F5F4', borderBottom: '1px solid var(--line-soft)' }}>
          <div style={{ maxWidth: 1240, margin: '0 auto', padding: '10px 20px', fontSize: 13, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>🏁</span>
            <span>This event has ended. It's shown here for reference only.</span>
          </div>
        </div>
      )}

      <div
        style={{ maxWidth: 1240, margin: '0 auto', padding: '28px 20px 60px', display: 'grid', gap: 36, alignItems: 'start' }}
        className="items-start"
        data-layout="event-detail"
      >
        <style>{`
          @media (min-width: 768px) { [data-layout="event-detail"] { grid-template-columns: minmax(0,1fr) 380px; padding: 28px 40px 60px; } }
        `}</style>
        {/* Left — content */}
        <div>
          <div className="flex gap-1.5 mb-3">
            <EventBadge type={event.eventType ?? 'potluck'} size="md" />
            {event.isRecurring && (
              <span className="chip h-[26px]">{getRecurrenceLabel(event.rrule)}</span>
            )}
          </div>

          <h1 className="serif m-0 text-[48px] leading-[1.02] tracking-[-0.025em]">
            {event.title}
          </h1>

          <div className="mt-2.5 text-[15px] text-ink-2 flex items-center gap-3.5 flex-wrap">
            <span className="inline-flex gap-1.5 items-center">
              {isOwnEvent
                ? <><Icons.home size={14} /> <span className="font-semibold" style={{ color: t.color }}>You're hosting</span></>
                : isChurchHosted
                  ? <><Icons.church size={14} /> Church event</>
                  : <><Icons.home size={14} /> Person-hosted</>
              }
            </span>
            {nextOccurrence?.rsvpCount !== undefined && (
              <span className="inline-flex gap-1.5 items-center text-muted">
                <Icons.users size={13} /> {nextOccurrence.rsvpCount} going
              </span>
            )}
            {!isOwnEvent && (
              <a
                href={`/api/v1/events/${event.id}/calendar.ics`}
                download
                className="text-[13px] text-primary inline-flex items-center gap-[5px] no-underline font-medium"
              >
                <Icons.cal size={13} /> Add to calendar
              </a>
            )}
            <button
              type="button"
              onClick={() => setShowShare(true)}
              className="text-[13px] text-primary inline-flex items-center gap-[5px] no-underline font-medium bg-transparent border-0 cursor-pointer p-0 font-sans"
            >
              <Icons.share size={13} /> Share
            </button>
            {!isOwnEvent && (
              <a
                href={`mailto:ddavenport420@gmail.com?subject=Report: ${encodeURIComponent(event.title)}&body=I'd like to report this event: ${encodeURIComponent(window.location.href)}`}
                className="text-[12px] text-muted ml-auto no-underline"
              >
                Report
              </a>
            )}
          </div>

          {/* Scraped event warning */}
          {isScraped && (
            <div className="mt-4 px-4 py-3 rounded-[10px] bg-[#fff8e1] border border-warn flex gap-2.5 items-start">
              <span className="text-[16px] shrink-0">⚠️</span>
              <div>
                <div className="text-[13px] font-semibold text-ink mb-[3px]">
                  This event was found online and may be inaccurate.
                </div>
                <div className="text-[12.5px] text-ink-2 leading-[1.5]">
                  Call the church to confirm details before attending.
                </div>
              </div>
            </div>
          )}

          {/* Location map */}
          {(() => {
            const showExact = event.hostedByType === 'church' || !event.hideAddressUntilApproved
            const mapLat = showExact
              ? event.locationLat
              : (event.locationPublicLat ?? event.locationLat)
            const mapLng = showExact
              ? event.locationLng
              : (event.locationPublicLng ?? event.locationLng)
            return (
              <div className="mt-[22px]">
                <EventLocationMap
                  lat={mapLat}
                  lng={mapLng}
                  label={event.addressText || event.title}
                  height={280}
                />
              </div>
            )
          })()}

          {/* Detail tiles */}
          {nextOccurrence && (
            <div className="mt-6 grid gap-2.5 grid-cols-2 sm:grid-cols-4">
              <DetailTile icon={<Icons.cal size={15} />} label="Next" value={formatDate(nextOccurrence.startsAt)} sub={event.isRecurring ? getRecurrenceLabel(event.rrule) : undefined} />
              <DetailTile icon={<Icons.clock size={15} />} label="Time" value={formatTime(nextOccurrence.startsAt)} />
              <DetailTile
                icon={<Icons.pin size={15} />}
                label="Address"
                value={
                  event.addressText && !event.hideAddressUntilApproved
                    ? event.addressText.split(',')[0]
                    : event.hideAddressUntilApproved
                      ? 'Private'
                      : '—'
                }
                sub={
                  event.addressText && !event.hideAddressUntilApproved
                    ? event.addressText.split(',').slice(1).join(',').trim() || undefined
                    : event.hideAddressUntilApproved
                      ? 'Shared after approval'
                      : undefined
                }
              />
              <DetailTile icon={<Icons.users size={15} />} label="Going" value={String(nextOccurrence.rsvpCount ?? 0)} />
            </div>
          )}

          {/* Tags and attributes */}
          {(event.tags.length > 0 || Object.keys(event.attributes ?? {}).some((k) => (event.attributes as Record<string, unknown>)[k])) && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {event.tags.map((tag) => (
                <span key={tag} className="chip">{tag}</span>
              ))}
              {(Object.keys(event.attributes ?? {}) as (keyof EventAttributes)[])
                .filter((k) => event.attributes[k])
                .map((k) => (
                  <span key={k} className="chip bg-primary-soft text-primary border-primary">
                    {ATTRIBUTE_LABELS[k] ?? k}
                  </span>
                ))}
            </div>
          )}

          {/* About */}
          {event.description && (
            <div className="mt-7">
              <div className="eyebrow mb-1.5">About this event</div>
              <p className="m-0 text-[15.5px] leading-[1.55] text-ink-2 max-w-[640px]">
                {event.description}
              </p>
            </div>
          )}

          {/* Dish coverage */}
          {dishItems && dishItems.length > 0 && (
            <div className="mt-8">
              <div className="flex justify-between items-baseline mb-3">
                <div>
                  <div className="eyebrow">What's needed</div>
                  <div className="serif text-[28px] mt-0.5">Dish coverage</div>
                </div>
                <div>
                  <div className="mono text-[11px] text-muted">CLAIMED</div>
                  <div className="serif text-[22px] text-good">{totalClaimed}/{totalNeeded}</div>
                </div>
              </div>
              <div className="card p-[18px]" style={{ borderLeft: `3px solid ${t.color}` }}>
                <DishSegments items={dishItems} />
              </div>
            </div>
          )}
          {/* Hosted by */}
          <HostedByCard hostedByType={event.hostedByType} hostedById={event.hostedById} />
        </div>

        {/* Right — sticky rail */}
        <aside className="sticky top-5">
          {/* Event image — shown above RSVP card when host uploaded one */}
          {event.imageUrl && (() => {
            const fallback = getDefaultImageForType(event.eventType ?? 'potluck', '800x480')
            return (
              <div className="mb-3 rounded-[14px] overflow-hidden" style={{ height: 200 }}>
                <img
                  src={event.imageUrl}
                  alt={event.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = fallback }}
                />
              </div>
            )
          })()}
          {isScraped ? (
            <div className="card p-[22px] bg-s2" style={{ borderLeft: '3px solid var(--muted)' }}>
              <div className="eyebrow mb-1">Unverified listing</div>
              <div className="serif text-[22px] mb-2.5">Interested?</div>
              <p className="text-[13px] text-ink-2 leading-[1.5] mb-[18px]">
                This event was found online and may have changed. Confirm with the church before attending.
              </p>

              <button
                onClick={() => trackPlanToAttend.mutate(undefined, {
                  onSuccess: (res) => {
                    const a = document.createElement('a')
                    a.href = res.data.icsUrl
                    a.download = ''
                    a.click()
                  },
                })}
                disabled={trackPlanToAttend.isPending}
                className="flex items-center justify-center gap-2 py-[11px] rounded-[10px] w-full bg-ink text-white text-[14px] font-semibold border-0 cursor-pointer disabled:opacity-70 mb-3 font-sans"
              >
                <Icons.cal size={14} /> Plan to attend
              </button>

              {event.churchPhone && (
                <a
                  href={`tel:${event.churchPhone}`}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-[10px] w-full border border-line text-ink text-[14px] font-medium no-underline mb-3"
                >
                  📞 {event.churchPhone}
                </a>
              )}

              {event.churchEmail && !inquire.isSuccess && (
                <div>
                  {!accessToken ? (
                    <Link
                      to="/login"
                      className="flex items-center justify-center gap-1.5 py-2.5 rounded-[10px] w-full border border-line bg-transparent text-ink text-[14px] font-medium no-underline font-sans"
                    >
                      ✉️ Log in to contact this church
                    </Link>
                  ) : !showInquiryForm ? (
                    <button
                      onClick={() => setShowInquiryForm(true)}
                      className="flex items-center justify-center gap-1.5 py-2.5 rounded-[10px] w-full border border-line bg-transparent text-ink text-[14px] font-medium cursor-pointer font-sans"
                    >
                      ✉️ Request information
                    </button>
                  ) : (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        inquire.mutate()
                      }}
                      className="flex flex-col gap-2"
                    >
                      <p className="m-0 text-[12.5px] text-ink-2">
                        We'll send your message from <strong>{user?.email}</strong>.
                      </p>
                      {inquire.isError && (
                        <p className="m-0 text-[12px] text-warn">
                          {(inquire.error as Error)?.message ?? 'Something went wrong'}
                        </p>
                      )}
                      <button
                        type="submit"
                        disabled={inquire.isPending}
                        className="py-2.5 rounded-[10px] bg-primary text-on-primary border-0 text-[14px] font-semibold cursor-pointer disabled:opacity-70 font-sans"
                      >
                        {inquire.isPending ? 'Sending…' : 'Send message'}
                      </button>
                    </form>
                  )}
                </div>
              )}

              {inquire.isSuccess && (
                <p className="m-0 text-[13px] text-primary font-medium text-center">
                  Sent! They'll get your message shortly.
                </p>
              )}
            </div>
          ) : isOwnEvent && nextOccurrence ? (
            <HostPanel event={event} occurrence={nextOccurrence} eventId={event.id} adminEditUrl={adminEditUrl} />
          ) : (existingRsvp || rsvpSuccess) ? (
            <div className="card p-[22px]" style={{ borderLeft: `3px solid ${t.color}` }}>
              <div className="text-center py-3">
                <div className="text-[32px] mb-2">✓</div>
                <div className="serif text-[22px] mb-1.5">
                  {(() => {
                    if (rsvpSuccess && !existingRsvp) {
                      return event.rsvpRequiresApproval ? 'Request sent!' : "You're going!"
                    }
                    const status = localAttendance ?? existingRsvp?.attendanceStatus
                    if (status === 'maybe') return "You might go."
                    if (status === 'not_going') return "You can't make it."
                    return "You're going!"
                  })()}
                </div>
                <p className="text-[13.5px] text-ink-2 leading-[1.5] mb-4">
                  {rsvpSuccess && event.rsvpRequiresApproval && !existingRsvp
                    ? "The host will review your request and send you the full address once approved."
                    : "See you there! Check above for the address."}
                </p>
                <div className="flex gap-2 justify-center mb-4">
                  {(['going', 'maybe', 'not_going'] as const).map((status) => {
                    const btnLabels = { going: 'Going', maybe: 'Maybe', not_going: "Can't go" }
                    const current = localAttendance ?? existingRsvp?.attendanceStatus ?? 'going'
                    const isActive = current === status
                    return (
                      <button
                        key={status}
                        type="button"
                        onClick={() => {
                          const rid = activeRsvpId ?? existingRsvp?.id
                          if (!rid) return
                          setLocalAttendance(status)
                          updateRsvp.mutate({ rsvpId: rid, attendanceStatus: status })
                        }}
                        style={{
                          padding: '5px 13px',
                          borderRadius: 999,
                          border: `1.5px solid ${isActive ? t.color : 'var(--line)'}`,
                          background: isActive ? t.soft : 'transparent',
                          color: isActive ? t.color : 'var(--ink-2)',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {btnLabels[status]}
                      </button>
                    )
                  })}
                </div>
                {(activeRsvpId ?? existingRsvp?.id) && (
                  <button
                    type="button"
                    onClick={() => {
                      const rid = activeRsvpId ?? existingRsvp?.id
                      if (!rid) return
                      deleteRsvp.mutate(rid, {
                        onSuccess: () => {
                          setRsvpSuccess(false)
                          setActiveRsvpId(undefined)
                          setLocalAttendance(null)
                        },
                      })
                    }}
                    className="bg-transparent border-0 text-[13px] text-warn cursor-pointer"
                  >
                    Cancel my RSVP
                  </button>
                )}
              </div>
            </div>
          ) : myRsvpLoading && user ? (
            null
          ) : isRsvpClosed ? (
            <div className="card p-[22px] bg-s2" style={{ borderLeft: '3px solid var(--muted)' }}>
              <div className="eyebrow mb-1 text-muted">RSVPs</div>
              <div className="serif text-[20px] mb-2 text-ink-2">
                {isPast ? 'This event has ended.' : 'RSVPs are closed.'}
              </div>
              <p className="text-[13px] text-muted leading-[1.5] m-0">
                {isPast
                  ? 'The event has already taken place.'
                  : 'The host has stopped accepting new RSVPs for this event.'}
              </p>
            </div>
          ) : (
            <div className="card p-[22px] bg-s2" style={{ borderLeft: `3px solid ${t.color}` }}>
              <div className="eyebrow mb-1">RSVP</div>
              {event.attributes?.bringADish && (
                <div className="serif text-[24px] mb-1">Bring a dish.</div>
              )}
              {event.attributes?.bringADish && dishItems && (() => {
                const mostNeeded = dishItems.find(d => d.got < d.need)
                return mostNeeded ? (
                  <div className="text-[12.5px] text-muted mb-4">
                    Most needed: <span className="text-warn font-medium">
                      {mostNeeded.name} ({mostNeeded.got}/{mostNeeded.need})
                    </span>
                  </div>
                ) : null
              })()}

              {nextOccurrence ? (
                <RsvpForm
                  occurrence={nextOccurrence}
                  event={event}
                  onSuccess={(rsvpId) => { setRsvpSuccess(true); setActiveRsvpId(rsvpId) }}
                />
              ) : (
                <p className="text-[13px] text-muted">No upcoming dates available.</p>
              )}
            </div>
          )}

{!isOwnEvent && !isScraped && event.hostedByType === 'person' && event.rsvpRequiresApproval && (
            <div className="mt-3.5 px-3.5 py-3 flex gap-2.5 items-center rounded-xl border border-line-soft bg-surface">
              <Icons.spark size={14} />
              <div className="text-[12px] text-ink-2 leading-[1.4]">
                This is a private home event. Be respectful of the host's space.
              </div>
            </div>
          )}
        </aside>
      </div>
      <ShareDrawer
        open={showShare}
        onClose={() => setShowShare(false)}
        url={`${window.location.origin}/events/${event.id}`}
        title={event.title}
      />
    </PublicLayout>
    </>
  )
}
